<template>
  <v-container class="menu-container">

       <!-- My Products -->
       <div
      v-if="!isLoadingProducts && myProducts.length > 0"
      class="menu-section my-products"
    >
      <h3 class="section-title">
        <v-icon size="18" class="mr-2" color="#EA5545">mdi-package-variant</v-icon>
        {{ t('menus.default.sections.myProducts') }}
      </h3>

      <!-- Products List -->
      <div class="products-list">
        <router-link
          v-for="product in myProducts"
          :key="product.myp_id"
          :to="{ name: 'vertrag', params: { id: product.myp_id } }"
          class="product-item"
        >
          <v-icon size="16" color="#EA5545" class="mr-2">mdi-package</v-icon>
          <span class="product-name">{{ product.prod_name }}</span>
        </router-link>
      </div>
    </div>
    <div class="menu-header">
      <router-link
        :to="{ name: 'help' }"
        class="support-cta"
      >
        <div class="support-cta-card">
         
          <div class="support-cta-headline">
            <div class="support-cta-icon">
              <v-icon color="#EA5545" size="36">mdi-shield-account</v-icon>
            </div>
            <div class="support-cta-content">
              <p class="support-cta-title">
                {{ t('menus.default.actions.startSupport') }}
              </p>
              <p class="support-cta-subtitle">
               Wir helfen Ihnen persönlich.
              </p>
            </div>
          </div>
          <div class="support-cta-body">
            <p class="support-cta-text">
              {{ t('menus.default.actions.startSupportDescription') }}
            </p>
            <span class="support-cta-link">
              {{ t('menus.default.actions.startSupportLink') }}
              <v-icon size="16" class="ml-1">mdi-arrow-right</v-icon>
            </span>
            <div
              v-if="!isLoadingRecent && recentTickets.length > 0"
              class="tickets-list compact support-inline-list"
            >
              <router-link
                v-for="ticket in recentTickets"
                :key="ticket.sup_id"
                :to="`/support/${ticket.sup_id}`"
                class="ticket-item"
              >
                <div class="ticket-header">
                  <div class="ticket-id-container">
                    <span class="ticket-id">#{{ ticket.sup_id }}</span>
                  </div>
                  <v-chip 
                    :color="getStatusColor(ticket.sup_status)" 
                    size="x-small"
                    class="status-chip"
                  >
                    {{ getStatusText(ticket.sup_status) }}
                  </v-chip>
                </div>
                <p class="ticket-title">{{ truncateText(ticket.sup_title, 60) }}</p>
                <div class="ticket-footer">
                  <span class="ticket-date">{{ formatDate(ticket.sup_created_date) }}</span>
                </div>
              </router-link>
              <div class="section-link-card inline-link-card">
                <router-link to="/support" class="section-link">
                  {{ t('menus.default.actions.viewSupportsButton') }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </router-link>

      <router-link
        :to="{ name: 'brain' }"
        class="support-cta"
      >
        <div class="support-cta-card">
         
          <div class="support-cta-headline">
            <div class="support-cta-icon">
              <v-icon color="#EA5545" size="36">mdi-brain</v-icon>
            </div>
            <div class="support-cta-content">
              <p class="support-cta-title">
                Clearment.AI Chat
              </p>
              <p class="support-cta-subtitle">
               KI-gestützte Vertragsanalyse.
              </p>
            </div>
          </div>
          <div class="support-cta-body">
            <p class="support-cta-text">
              Stelle Fragen zu deinen Verträgen und erhalte sofortige Antworten basierend auf dem Clearment Klarvertrag.
            </p>
            <span class="support-cta-link">
              Clearment.AI Chat starten
              <v-icon size="16" class="ml-1">mdi-arrow-right</v-icon>
            </span>
          </div>
        </div>
      </router-link>
    </div>

 

    <!-- Unread Support Tickets -->
    <div
      class="menu-section recent-tickets"
      v-if="!isLoadingUnread && unreadTickets.length > 0"
    >
      <h3 class="section-title">
        <v-icon size="18" class="mr-2" color="#EA5545">mdi-clock-outline</v-icon>
        {{ t('menus.default.sections.unreadTickets') }}
      </h3>

      <div class="tickets-list">
        <router-link
          v-for="ticket in unreadTickets"
          :key="ticket.sup_id"
          :to="`/support/${ticket.sup_id}`"
          :class="['ticket-item', { 'has-unread': ticket.unread_count > 0 }]"
        >
          <div class="ticket-header">
            <div class="ticket-id-container">
              <span class="ticket-id">#{{ ticket.sup_id }}</span>
              <v-badge
                v-if="ticket.unread_count > 0"
                :content="ticket.unread_count"
                color="#EA5545"
                inline
                class="unread-badge-small"
              >
              </v-badge>
            </div>
            <v-chip 
              :color="getStatusColor(ticket.sup_status)" 
              size="x-small"
              class="status-chip"
            >
              {{ getStatusText(ticket.sup_status) }}
            </v-chip>
          </div>
          <p class="ticket-title">{{ truncateText(ticket.sup_title, 45) }}</p>
          <div class="ticket-footer">
            <span class="ticket-date">{{ formatDate(ticket.sup_created_date) }}</span>
            <span v-if="ticket.unread_count > 0" class="new-messages-indicator">
              <v-icon size="12" color="#EA5545">mdi-circle</v-icon>
              {{ t('menus.default.states.newIndicator') }}
            </span>
          </div>
        </router-link>
      </div>
    </div>

    <div
      v-if="!isLoadingBrain && recentBrainRequests.length > 0"
      class="menu-section brain-preview"
    >
      <h3 class="section-title">
        <v-icon size="18" class="mr-2" color="#EA5545">mdi-brain</v-icon>
        {{ t('menus.default.sections.brain') }}
      </h3>
      <div class="brain-list">
        <router-link
          v-for="request in recentBrainRequests"
          :key="request.breq_id"
          :to="request.breq_product_id
            ? { name: 'brain', params: { productid: request.breq_product_id, id: request.breq_id } }
            : { name: 'brain', params: { id: request.breq_id } }"
          class="brain-item"
        >
          <div class="brain-item-header">
            <v-chip size="x-small" color="#EA5545" variant="tonal">
              #{{ request.breq_id }}
            </v-chip>
            <span class="brain-date">{{ formatDate(request.breq_created_date) }}</span>
          </div>
          <p class="brain-title">{{ truncateText(request.breq_prompt, 80) }}</p>
        </router-link>
      </div>

      <div class="section-link-card">
        <router-link to="/brain" class="section-link">
          {{ t('menus.default.actions.viewBrain') }}
        </router-link>
      </div>
    </div>
  </v-container>
</template>

<script setup>
import { ref, onMounted, inject, computed } from 'vue'
import { useLanguage } from '@/composables/useLanguage'

const baseCalls = inject('baseCalls')
const { languageStore, t } = useLanguage()

// State
const unreadTickets = ref([])
const recentTickets = ref([])
const recentBrainRequests = ref([])
const isLoadingUnread = ref(false)
const isLoadingRecent = ref(false)
const isLoadingBrain = ref(false)
const myProducts = ref([])
const isLoadingProducts = ref(false)

const currentLocale = computed(() => {
  const lang = languageStore?.currentLanguage?.value || 'de'
  return lang === 'de' ? 'de-DE' : 'en-US'
})

// Functions
function getStatusColor(status) {
  const colors = {
    0: 'orange',
    1: 'green',
    2: 'grey',
    3: 'red'
  }
  return colors[status] || 'grey'
}

function getStatusText(status) {
  const texts = {
    0: 'supportIndex.status.open',
    1: 'supportIndex.status.resolved',
    2: 'supportIndex.status.closed',
    3: 'supportIndex.status.urgent'
  }
  const key = texts[status] || 'supportIndex.status.unknown'
  return t(key)
}

function formatDate(dateString) {
  if (!dateString || dateString === '1973-01-01 00:00:00') return t('common.labels.notAvailable')
  
  const date = new Date(dateString)
  const now = new Date()
  const diffInMs = now - date
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInMinutes < 1) return t('menus.default.time.justNow')
  if (diffInMinutes < 60) return t('menus.default.time.minutesAgo', { value: diffInMinutes })
  if (diffInHours < 24) return t('menus.default.time.hoursAgo', { value: diffInHours })
  if (diffInDays < 7) return t('menus.default.time.daysAgo', { value: diffInDays })
  
  return date.toLocaleDateString(currentLocale.value, { 
    month: 'short', 
    day: 'numeric'
  })
}

function truncateText(text, maxLength) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

async function loadUnreadTickets() {
  isLoadingUnread.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getunreadsupports',
      limit: 3
    })
    
    if (response.data.success && response.data.supports) {
      unreadTickets.value = response.data.supports || []
    } else {
      unreadTickets.value = []
    }
  } catch (err) {
    console.error('Error loading unread tickets:', err)
    unreadTickets.value = []
  } finally {
    isLoadingUnread.value = false
  }
}

async function loadRecentTickets() {
  isLoadingRecent.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getAllMySupports'
    })

    if (response.data.success && response.data.supports) {
      recentTickets.value = response.data.supports.slice(0, 3)
    } else {
      recentTickets.value = []
    }
  } catch (err) {
    console.error('Error loading recent tickets:', err)
    recentTickets.value = []
  } finally {
    isLoadingRecent.value = false
  }
}

async function loadMyProducts() {
  isLoadingProducts.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getmyproducts'
    })
    
    if (response.data.success && response.data.products) {
      myProducts.value = response.data.products
    }
  } catch (err) {
    console.error('Error loading products:', err)
  } finally {
    isLoadingProducts.value = false
  }
}

async function loadRecentBrainRequests() {
  isLoadingBrain.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getmybrainrequests'
    })

    if (response.data.success && Array.isArray(response.data.requests)) {
      recentBrainRequests.value = response.data.requests.slice(0, 3)
    } else {
      recentBrainRequests.value = []
    }
  } catch (err) {
    console.error('Error loading brain requests:', err)
    recentBrainRequests.value = []
  } finally {
    isLoadingBrain.value = false
  }
}

onMounted(() => {
  loadUnreadTickets()
  loadRecentTickets()
  loadMyProducts()
  loadRecentBrainRequests()
})
</script>

<style scoped src="@/assets/styles/sfc/components/sites/modules/MenueDefault.css"></style>
